import { createSlice } from "@reduxjs/toolkit";
import { fetchUserStatsAsync } from "../thunks/OrganizationThunk";
import {
  createOrganizationAsync,
  createUserAsync,
  fetchOrganizationDetailsAsync,
  fetchOrganizationHierarchyAsync,
  fetchOrganizationListAsync,
  fetchOrganizationSummaryAsync,
  getUserAccountDetailsAsync,
  loginUserAsync,
  loginWithGoogleAsync,
} from "../thunks/OrganizationThunk";

export interface User {
  id: string;
  first: string;
  last: string;
  email: string;
  external_unique_id: string;
  external_booking_id: string;
}
export interface Organization {
  id: number;
  name: string;
  displayName: string;
  contactName: string;
  contactEmail: string;
  parentOrgId: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface OrganizationList {
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
  orgId?: number;
  items: Organization[];
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
}
interface UserAccount{
email: string;
email_verified: boolean;
external_unique_id: string|null;
id:string;
job_title: string|null;
first:string|null;
last: string|null;
middle: string|null;
mobile_phone: string|null;
organization_id: string|null;
user_role: string|null;
}

export const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  IDLE: "idle",
  FAILED: "failed",
};
const initialOrganizationListState: OrganizationListState = {
  OrganizationList: null,
  loginResponse: null,
  loginStatus: STATUS.IDLE,
  createOrgStatus: STATUS.IDLE,
  createOrgResponse: null,
  createUserStatus: STATUS.IDLE,
  createUserResponse: null,
  status: STATUS.IDLE,
  error: null,
  userAccount: null,
  ParentOrganizationList: null,
  selectedParentOrgId: null,
  selectedOrgDetails: null,
  parentHierarchyOrganizationList: null,
  orgSummary: null,
  allUserStats: null,
  loginRetryUntil: null,
};

interface OrganizationListState {
  OrganizationList: any | null;
  loginResponse: LoginResponse | null;
  loginStatus: string;
  createOrgStatus: string;
  createOrgResponse: any;
  createUserStatus: string;
  createUserResponse: any;
  status: string;
  error: string | null;
  userAccount: UserAccount | null;
  ParentOrganizationList: OrganizationList | null;
  selectedParentOrgId: number | null;
  selectedOrgDetails: any;
  parentHierarchyOrganizationList: Organization[] | null;
  orgSummary: any;
  allUserStats: any | null;
  loginRetryUntil: number | null;
}


const DelearDetailsSlice = createSlice({
  name: "OrganizationList",
  initialState: initialOrganizationListState,
  reducers: {

    resetLoginStatus: state => {
      state.loginStatus = STATUS.IDLE;
    },
    resetCreateOrgStatus: state => {
      state.createOrgStatus = STATUS.IDLE;
    },
    resetCreateUserStatus: state => {
      state.createUserStatus = STATUS.IDLE;
    },
    setParentOrgId: (state,action) => {
      state.selectedParentOrgId = action.payload;
    },
    logout: (state) => {
      // Reset all state to initial values
      state.OrganizationList = null;
      state.loginResponse = null;
      state.loginStatus = STATUS.IDLE;
      state.userAccount = null;
      state.ParentOrganizationList = null;
      state.selectedParentOrgId = null;
      state.selectedOrgDetails = null;
      state.parentHierarchyOrganizationList = null;
      state.orgSummary = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationListAsync.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchOrganizationListAsync.fulfilled, (state, action) => {
        state.OrganizationList = action.payload;

       

        state.status = STATUS.SUCCESS;
      })
      .addCase(fetchOrganizationListAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })

      .addCase(loginUserAsync.pending, (state) => {
        state.loginStatus = STATUS.LOADING;
        state.error = null;
        state.loginRetryUntil = null;
      })
      .addCase(loginUserAsync.fulfilled, (state, action) => {
        state.loginResponse = action.payload;
        state.loginStatus = STATUS.SUCCESS;
        state.loginRetryUntil = null;
      })
      .addCase(loginUserAsync.rejected, (state, action) => {
        state.loginStatus = STATUS.FAILED;
        const payload = action.payload as any;
        state.error = payload?.message ?? action.error.message ?? null;
        state.loginRetryUntil = payload?.retryAfterSeconds
          ? Date.now() + payload.retryAfterSeconds * 1000
          : null;
      })
      .addCase(loginWithGoogleAsync.pending, (state) => {
        state.loginStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(loginWithGoogleAsync.fulfilled, (state, action) => {
        state.loginResponse = action.payload;
        state.loginStatus = STATUS.SUCCESS;
      })
      .addCase(loginWithGoogleAsync.rejected, (state, action) => {
        state.loginStatus = STATUS.FAILED;
        state.error = action.payload as string ?? action.error.message ?? null;
      })
      .addCase(createOrganizationAsync.pending, (state) => {
        state.createOrgStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(createOrganizationAsync.fulfilled, (state, action) => {
        state.createOrgResponse = action.payload;
        state.createOrgStatus = STATUS.SUCCESS;
      })
      .addCase(createOrganizationAsync.rejected, (state, action) => {
        state.createOrgStatus = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })


      .addCase(createUserAsync.pending, (state) => {
        state.createUserStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(createUserAsync.fulfilled, (state, action) => {
        state.createUserResponse = action.payload;
        state.createUserStatus = STATUS.SUCCESS;
      })
      .addCase(createUserAsync.rejected, (state, action) => {
        state.createUserStatus = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })
      .addCase(getUserAccountDetailsAsync.pending, (state) => {
        state.error = null;
        state.status = STATUS.LOADING;
      })
      .addCase(getUserAccountDetailsAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.userAccount = action.payload;
      })
      .addCase(getUserAccountDetailsAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchOrganizationDetailsAsync.pending, (state) => {
        state.error = null;
        state.status = STATUS.LOADING;
      })
      .addCase(fetchOrganizationDetailsAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.selectedOrgDetails = action.payload;
      })
      .addCase(fetchOrganizationDetailsAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchOrganizationHierarchyAsync.pending, (state) => {
        state.error = null;
        state.status = STATUS.LOADING;
      })
      .addCase(fetchOrganizationHierarchyAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.parentHierarchyOrganizationList = action.payload;
      })
      .addCase(fetchOrganizationHierarchyAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })


      .addCase(fetchOrganizationSummaryAsync.pending, (state) => {
        state.error = null;
        state.status = STATUS.LOADING;
      })
      .addCase(fetchOrganizationSummaryAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.orgSummary = action.payload;
      })
      .addCase(fetchOrganizationSummaryAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })

      // Add allUserStats handling
      .addCase(fetchUserStatsAsync.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchUserStatsAsync.fulfilled, (state, action) => {
        state.status = STATUS.SUCCESS;
        state.allUserStats = action.payload;
      })
      .addCase(fetchUserStatsAsync.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      });
  },
});
export const { resetLoginStatus, resetCreateOrgStatus, resetCreateUserStatus, setParentOrgId, logout } = DelearDetailsSlice.actions;
export default DelearDetailsSlice.reducer;
